import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

const toEuros = (cents) => (cents / 100).toFixed(2);
const toCents = (v) => Math.round(parseFloat(String(v).replace(",", ".")) * 100) || 0;

export default function AdminTarifs() {
  const [tarifs, setTarifs] = useState(null);
  const [cotisation, setCotisation] = useState("");
  const [fsgt, setFsgt] = useState("");
  const [fsgtEcole, setFsgtEcole] = useState("");
  const [ffc, setFfc] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tarifs");
      setTarifs(data);
      setCotisation(toEuros(data.cotisation_cents));
      setFsgt(toEuros(data.fsgt_cents));
      setFsgtEcole(toEuros(data.fsgt_ecole_cents));
      setFfc(data.ffc.map((f) => ({ ...f, euros: toEuros(f.prix_cents) })));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/tarifs", {
        cotisation_cents: toCents(cotisation),
        fsgt_cents: toCents(fsgt),
        fsgt_ecole_cents: toCents(fsgtEcole),
        ffc: ffc.map((f) => ({ id: f.id, prix_cents: toCents(f.euros) })),
      });
      toast.success("Tarifs enregistrés — appliqués immédiatement au formulaire d'inscription");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!tarifs) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>;
  }

  return (
    <main className="max-w-[1000px] mx-auto px-5 sm:px-8 py-10">
      <form onSubmit={save}>
        <p className="text-steel text-sm mb-8" data-testid="tarifs-saison">
          Tarifs appliqués au formulaire d'inscription — saison <strong>{tarifs.saison}</strong>. Toute modification est
          prise en compte immédiatement dans le calcul du total.
        </p>

        <div className="bg-white border border-navy/10 p-6 sm:p-8 mb-6">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-6">Cotisation club (obligatoire)</h2>
          <div className="max-w-xs">
            <label className={labelCls} htmlFor="tarif-cotisation">Montant (€)</label>
            <input id="tarif-cotisation" data-testid="tarif-cotisation" required inputMode="decimal" value={cotisation} onChange={(e) => setCotisation(e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="bg-white border border-navy/10 p-6 sm:p-8 mb-6">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-6">Licences FFC {tarifs.saison}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ffc.map((f) => (
              <div key={f.id}>
                <label className={labelCls} htmlFor={`tarif-ffc-${f.id}`}>{f.label}</label>
                <input
                  id={`tarif-ffc-${f.id}`}
                  data-testid={`tarif-ffc-${f.id}`}
                  required
                  inputMode="decimal"
                  value={f.euros}
                  onChange={(e) => setFfc((prev) => prev.map((x) => (x.id === f.id ? { ...x, euros: e.target.value } : x)))}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-navy/10 p-6 sm:p-8 mb-6">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-2">Licence FSGT {tarifs.saison}</h2>
          <p className="text-sm text-steel mb-6">
            Deux tarifs distincts, jamais mélangés : le tarif <strong>adulte</strong> s'applique aux licenciés club, le tarif
            <strong> École de vélo</strong> aux jeunes dont la catégorie a été validée « École de vélo » (option facultative du pack).
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <label className={labelCls} htmlFor="tarif-fsgt">FSGT adulte / licencié club (€)</label>
              <input id="tarif-fsgt" data-testid="tarif-fsgt" required inputMode="decimal" value={fsgt} onChange={(e) => setFsgt(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="tarif-fsgt-ecole">FSGT École de vélo — jeunes (€)</label>
              <input id="tarif-fsgt-ecole" data-testid="tarif-fsgt-ecole" required inputMode="decimal" value={fsgtEcole} onChange={(e) => setFsgtEcole(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="bg-navy text-white p-6 sm:p-8 mb-6" data-testid="tarifs-pack-ecole">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading uppercase font-semibold tracking-tight text-xl">Pack École de vélo {tarifs.saison}</h2>
              <p className="text-white/70 text-xs uppercase tracking-widest mt-1">Adhésion club + licence FFC + 1 maillot + 1 cuissard + 1 veste</p>
            </div>
            <p className="font-heading font-semibold text-3xl" data-testid="tarifs-pack-prix">{toEuros(tarifs.pack_ecole_cents).replace(".", ",")} €</p>
          </div>
          <p className="mt-4 text-xs text-white/60">
            Montant fixe imposé par le serveur (non modifiable ici). La licence FSGT n'est jamais ajoutée automatiquement au pack :
            elle reste une option facultative au tarif École de vélo ci-dessus.
          </p>
        </div>

        <div className="bg-mist border border-navy/10 p-6 sm:p-8 mb-8">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-3">Équipements</h2>
          <p className="text-sm text-steel" data-testid="tarifs-equipements-note">
            La vente d'équipements n'est plus gérée sur le site : les licenciés sont redirigés vers la boutique
            partenaire officielle (expressoshop.fr).
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="tarifs-save"
          className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          Enregistrer les tarifs
        </button>
      </form>
    </main>
  );
}
